import { Fragment } from 'react';
import { ChevronRightIcon, Chip, cn, ICON_COLOR } from '@zep-us/zus';
import { type CategoryTree } from '@/shared/category/type';
import { type CategoryLabels } from '../types';
import { combineSchooInfoLabels } from '../utils';

/**
 * 카테고리 정보 Chips 컴포넌트 (Read-only)
 *
 * Props로 받은 labels를 기반으로 Chip들을 표시합니다.
 * Context나 state에 의존하지 않는 순수 presentational 컴포넌트입니다.
 *
 * @example
 * <CategoryChips
 *   labels={{
 *     schoolInfoLabel: "초등학교 1-1",
 *     subjectLabel: "수학",
 *     publisherLabel: "비상"
 *   }}
 *   schoolTypeId="101"
 * />
 */
export const CategoryChips = ({
  labels,
  schoolTypeId,
  schoolInfoTree
}: {
  labels: CategoryLabels;
  schoolTypeId?: string;
  schoolInfoTree: CategoryTree;
}) => {
  // 학교급 타입에 따른 색상 결정
  const schoolType = schoolInfoTree.children.find(child => child.categoryTreeId === schoolTypeId)?.name?.toLowerCase();
  const schoolInfoLabel = combineSchooInfoLabels({
    schoolTypeLabel: labels.schoolTypeLabel,
    gradeLabel: labels.gradeLabel,
    semesterLabel: labels.semesterLabel
  });
  return <div className="flex flex-wrap items-center gap-3xs" data-sentry-component="CategoryChips" data-sentry-source-file="CategoryChips.tsx">
      {/* 학교 정보 Chip (학교급 + 학년 + 학기) */}
      {schoolInfoLabel && <CategoryChipBase className={cn('bg-neutral', schoolType === 'high' && 'bg-pink-50 border-opacity-assistive', schoolType === 'middle' && 'bg-blue-50 border-opacity-assistive', schoolType === 'primary' && 'bg-green-50 border-opacity-assistive')}>
          <span>{schoolInfoLabel}</span>
        </CategoryChipBase>}

      {/* 과목 Chip */}
      {labels.subjectLabel && <Fragment>
          <ChevronRightIcon size="sm" fill={ICON_COLOR.neutral} />
          <CategoryChipBase className="border-alternative">
            {labels.subjectLabel}
          </CategoryChipBase>
        </Fragment>}

      {/* 출판사 Chip */}
      {labels.publisherLabel && <Fragment>
          <ChevronRightIcon size="sm" fill={ICON_COLOR.neutral} />
          <CategoryChipBase className="border-alternative">
            {labels.publisherLabel}
          </CategoryChipBase>
        </Fragment>}
    </div>;
};
const CategoryChipBase = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <Chip className={cn('bg-default flex min-w-[40px] items-center justify-center border-opacity-assistive rounded-xs', 'px-xs py-3xs', 'text-body-sm-semibold text-neutral', className)} data-sentry-element="Chip" data-sentry-component="CategoryChipBase" data-sentry-source-file="CategoryChips.tsx">
      {children}
    </Chip>;
};